import { useGet, usePost, useQueryWithErrorHandling } from '@app/sdk';
import { useQueryClient } from '@tanstack/react-query';
import { useAccountIdContext } from '../../components/account/AccountIdContext';
import { useGlobalContext } from '../../GlobalContextProvider';

const pillarCode = "cl";

export function useMerchantGatewayService() {
  const get = useGet(true, pillarCode);
  const post = usePost(true, pillarCode);
  const { billingDate } = useAccountIdContext();
  const { merchantId } = useGlobalContext();

  async function getDeclinedAuthorizations(accountId: string) {
    return await get<{ authorisations: any[] }>(
      `/api/proxy/cl/merchant-gateway-service/account/${accountId}/authorisation?status=Declined`,
      {
        'context-merchantId': merchantId,
      }
    );
  }

  async function getSpendOptions(accountId: string, basketAmount: number) {
    return await get<any>(
      `/api/proxy/cl/merchant-gateway-service/account/${accountId}/spend-options?basketAmount=${basketAmount}`,
      {
        'context-merchantId': merchantId,
      }
    );
  }

  async function auth(
    accountId: string,
    amount: number,
    description: string,
    spendType: string,
    spendOptionId: string = undefined
  ) {
    const body = {
      authorizationDate: billingDate.toISOString(),
      amount: {
        value: amount,
        currencyCode: 'GBP',
      },
      description: `${description}`,
      spendType,
      spendOptionId,
    };

    return await post(`/api/proxy/cl/merchant-gateway-service/account/${accountId}/authorisation`, body, {
      'context-merchantId': merchantId,
    });
  }

  async function reverse(accountId, authorizationId, amount) {
    const body = {
      amount: {
        value: amount,
        currencyCode: 'GBP',
      },
    };
    return await post(
      `/api/proxy/cl/merchant-gateway-service/account/${accountId}/authorisation/${authorizationId}/reverse`,
      body,
      {
        'context-merchantId': merchantId,
      }
    );
  }

  function clear(accountId: string, authorizationId: string, amount: number, description: string) {
    const body = {
      authorizationId,
      amount: {
        value: amount,
        currencyCode: 'GBP',
      },
      transactionDate: billingDate.toISOString(),
      description: `${description}`,
      balanceMovementType: 'Purchase',
    };
    return post(
      `/api/proxy/cl/merchant-gateway-service/account/${accountId}/authorisation/${authorizationId}/settle`,
      body,
      {
        'context-merchantId': merchantId,
      }
    );
  }

  function refund(accountId: string, amount: number, description: string, originalTransactionId?: string) {
    const body = {
      amount: {
        value: amount,
        currencyCode: 'GBP',
      },
      description,
    } as any;

    return originalTransactionId
      ? post(
          `/api/proxy/cl/merchant-gateway-service/account/${accountId}/transaction/${originalTransactionId}/refund`,
          body,
          {
            'context-merchantId': merchantId,
          }
        )
      : post(`/api/proxy/cl/merchant-gateway-service/account/${accountId}/transaction/refund`, body, {
          'context-merchantId': merchantId,
        });
  }

  return {
    getDeclinedAuthorizations,
    getSpendOptions,
    auth,
    reverse,
    clear,
    refund,
  };
}

export function useGetDeclinedAuthorizations({ accountId }: { accountId: string }) {
  const { getDeclinedAuthorizations } = useMerchantGatewayService();
  return useQueryWithErrorHandling({
    queryKey: ['getDeclinedAuthorizations', { accountId }],
    queryFn: async () => await getDeclinedAuthorizations(accountId),
    enabled: !!accountId,
    select: (data) => data.authorisations,
  });
}

export function useRefreshGetDeclinedAuthorizations() {
  const queryClient = useQueryClient();
  return () =>
    queryClient.invalidateQueries({
      queryKey: ['getDeclinedAuthorizations'],
    });
}

export function useGetSpendOptions({ accountId, basketAmount }: { accountId: string; basketAmount: number }) {
  const { getSpendOptions } = useMerchantGatewayService();
  return useQueryWithErrorHandling({
    queryKey: ['getSpendOptions', { accountId, basketAmount }],
    queryFn: () => getSpendOptions(accountId, basketAmount),
    enabled: !!accountId && !!basketAmount,
    select: (data) => {
      const product = {
        instalment: data.options
          .filter((t) => t.offerSource == 'Product' && t.spendType == 'Instalment')
          .sort((a, b) => a.duration < b.duration),
        purchase: data.options.filter(
          (t) => /*TODO re-add when purchase has spend type t.offerSource == 'Product' &&*/ t.spendType == 'Purchase'
        ),
        bnpl: data.options.filter((t) => t.offerSource == 'Product' && t.spendType == 'BNPL'),
      };
      const promotional = {
        instalment: data.options
          .filter((t) => t.offerSource == 'Promotional' && t.spendType == 'Instalment')
          .sort((a, b) => a.duration < b.duration),
        purchase: data.options.filter((t) => t.offerSource == 'Promotional' && t.spendType == 'Purchase'),
        bnpl: data.options.filter((t) => t.offerSource == 'Promotional' && t.spendType == 'BNPL'),
      };
      return { product, promotional };
    },
  });
}

export function useRefreshGetSpendOptions() {
  const queryClient = useQueryClient();
  return () =>
    queryClient.invalidateQueries({
      queryKey: ['getSpendOptions'],
    });
}